import { createContext, useReducer, useContext, useEffect } from "react";
import { productsContent } from "../constants"; // Importing product list

// Define action types
const ADD_TO_WISHLIST = "ADD_TO_WISHLIST";
const REMOVE_FROM_WISHLIST = "REMOVE_FROM_WISHLIST";
const UPDATE_WISHLIST = "UPDATE_WISHLIST";

// Get wishlist from localStorage
const getWishlistFromStorage = () => {
  if (typeof window !== "undefined") {
    const storedWishlist = localStorage.getItem("wishlist");
    return storedWishlist ? JSON.parse(storedWishlist) : [];
  }
  return [];
};

// Function to check product availability
const checkProductAvailability = (productId) => {
  const product = productsContent.find(product => product.id === productId);
  return product?.inStock ? "In Stock" : "Out of Stock";
};

// Wishlist reducer function
const wishlistReducer = (state, action) => {
  switch (action.type) {
    case ADD_TO_WISHLIST:
      if (!state.some((item) => item.id === action.payload.id)) {
        const options = { month: "long", day: "numeric", year: "numeric" };
        const currentDate = new Date().toLocaleDateString("en-US", options);
        return [...state, { ...action.payload, dateAdded: currentDate, stockStatus: checkProductAvailability(action.payload.id) }];
      }
      return state;

    case REMOVE_FROM_WISHLIST:
      return state.filter((item) => item.id !== action.payload.id);

    case UPDATE_WISHLIST:
      return action.payload; // Update wishlist with new stock statuses

    default:
      return state;
  }
};

// Create Context
const WishlistContext = createContext();

// Wishlist Provider Component
export const WishlistProvider = ({ children }) => {
  const [wishlist, dispatch] = useReducer(wishlistReducer, [], getWishlistFromStorage);

  // Sync with localStorage and update stock statuses dynamically
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem("wishlist", JSON.stringify(wishlist));
    }
  
    // Update stock status dynamically
    const updatedWishlist = wishlist.map((item) => {
      const latestProduct = productsContent.find((product) => product.id === item.id);
      return {
        ...item,
        inStock: latestProduct ? latestProduct.inStock : false, // Ensure correct stock status
      };
    });
  
    dispatch({ type: UPDATE_WISHLIST, payload: updatedWishlist });
  
  }, [productsContent]); // Depend on productsContent to track stock updates


  // Actions
  const addToWishlist = (item) => {
    const latestProduct = productsContent.find(product => product.id === item.id);
  
    dispatch({
      type: ADD_TO_WISHLIST,
      payload: { 
        ...item, 
        inStock: latestProduct ? latestProduct.inStock : false // Store current stock status
      },
    });
  };
  

  const removeFromWishlist = (item) => {
    dispatch({ type: REMOVE_FROM_WISHLIST, payload: item });
  };

  return (
    <WishlistContext.Provider value={{ wishlist, addToWishlist, removeFromWishlist, checkProductAvailability }}>
      {children}
    </WishlistContext.Provider>
  );
};

// Custom Hook for using Wishlist Context
export const useWishlist = () => useContext(WishlistContext);